import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'

/**
 * Out-of-office / covering-RS coverage windows (WPS-160 §1g). A window is a
 * plain "X is out, Y covers over [start, end]" record; the zone resolver
 * already honours it and it self-reverts when it lapses. Gated (like the whole
 * assignment surface) on the `advanced_routing` capability, checked by the
 * caller; the backend 404s otherwise.
 */

export interface CoverageUser {
  uuid: string
  name: string
}

export interface CoverageWindow {
  uuid: string
  out_user: CoverageUser | null
  covering_user: CoverageUser | null
  starts_at: string
  ends_at: string
  reason: string
  active: boolean
}

export interface CoverageData {
  windows: CoverageWindow[]
  members: CoverageUser[]
}

export interface CreateCoveragePayload {
  out_user_uuid: string
  covering_user_uuid: string
  starts_at: string
  ends_at: string
  reason?: string
}

export function useClaimCoverage(enabled: boolean) {
  return useQuery<CoverageData>({
    queryKey: ['claim-coverage'],
    enabled,
    queryFn: () =>
      api.get('/claims/coverage/').then((r) => r.data?.result ?? r.data),
  })
}

export function useCreateCoverage() {
  const qc = useQueryClient()
  return useMutation<CoverageWindow, Error, CreateCoveragePayload>({
    mutationFn: (body) =>
      api.post('/claims/coverage/', body).then((r) => r.data?.result ?? r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim-coverage'] }),
  })
}

export function useDeleteCoverage() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, string>({
    mutationFn: (uuid) =>
      api.delete(`/claims/coverage/${uuid}/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claim-coverage'] }),
  })
}
